<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import UserAvatar from "@/components/shared/UserAvatar.vue";
import { useAuthStore } from "@/stores/authStore";
import { useCanAccessPlatformControl } from "@/composables/shared/useSectionPermissions";
import { useStopImpersonatingSystemPracticeStaff } from "@/composables/models/staff/useSystemPracticeStaff";
import SentryFeedbackButton from "@/components/ui/SentryFeedbackButton.vue";
import { useEnvironmentStore, EEnvironmentType } from "@/stores/environmentStore";

const props = defineProps<{
  compacted: boolean;
}>();

const emit = defineEmits<{
  (event: "open-tutorial"): void;
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const { loggedAsStaff, isImpersonating } = storeToRefs(authStore);
const canAccessPlatformControl = useCanAccessPlatformControl();
const { mutateAsync: stopImpersonatingSystemPracticeStaff } =
  useStopImpersonatingSystemPracticeStaff();

// Still under evaluation — only shown on DEV, MR, DEVEL, and QA builds, same
// convention as other environment-gated features (see EEnvironmentType usages).
const { currentEnv } = useEnvironmentStore();
const PERMISSIONS_MENU_ENVIRONMENTS = new Set<string>([
  EEnvironmentType.DEV,
  EEnvironmentType.MR,
  EEnvironmentType.DEVEL,
  EEnvironmentType.QA,
]);
const canShowPermissions = computed(() => PERMISSIONS_MENU_ENVIRONMENTS.has(currentEnv));

const avatarSize = computed(() => (props.compacted ? 34 : 40));

const labelClass =
  "tw:inline-flex tw:items-center tw:rounded-full tw:text-[9px] tw:leading-none tw:font-bold tw:tracking-[0.06em] tw:whitespace-nowrap tw:text-white tw:shadow-[0_1px_3px_rgba(14,43,58,0.25)]";

const stopImpersonating = () => {
  void stopImpersonatingSystemPracticeStaff();
};
</script>

<template>
  <div data-tour="dashboard-account">
    <v-menu
      content-class="orbis-menu"
      location="bottom"
      :offset="11"
    >
      <template #activator="{ props: avatarMenuProps }">
        <v-btn
          v-bind="avatarMenuProps"
          icon
          variant="text"
          :aria-label="t('generic.entity.settings')"
          :size="avatarSize"
          class="navbar-user-menu-btn tw:!relative tw:!mr-2 tw:!overflow-visible tw:rounded-[10px]! tw:bg-surface! tw:shadow-[0_2px_6px_rgba(14,43,58,0.08)] tw:ring-1 tw:ring-on-surface/10 tw:transition-transform tw:duration-200 tw:ease-[cubic-bezier(0.34,1.56,0.64,1)] tw:hover:-translate-y-px"
          data-testid="user-menu-button"
        >
          <user-avatar
            :size="avatarSize"
            :user="loggedAsStaff"
            class="tw:!transition-all tw:!duration-500"
            hide-badges
            rounded="0"
          />

          <div
            v-if="loggedAsStaff?.badges?.[0] || isImpersonating"
            class="tw:pointer-events-none tw:absolute tw:bottom-[-7px] tw:left-1/2 tw:z-content-overlay tw:flex tw:-translate-x-1/2 tw:items-center tw:gap-0.5"
          >
            <span
              v-if="loggedAsStaff?.badges?.[0]"
              :class="[labelClass, 'tw:bg-primary tw:px-1.5 tw:py-[3px]']"
              :style="{ backgroundColor: `#${loggedAsStaff.badges[0].color}` }"
            >
              {{ loggedAsStaff.badges[0].code }}
            </span>

            <span
              v-if="isImpersonating"
              :class="[labelClass, 'tw:bg-purple tw:px-1 tw:py-0.5']"
            >
              <v-icon
                icon="mdi-incognito"
                size="10"
              />
            </span>
          </div>
        </v-btn>
      </template>

      <v-list
        density="compact"
        class="user-menu-list"
        data-testid="user-menu-dropdown"
      >
        <template v-if="isImpersonating">
          <div class="tw:mx-1 tw:mb-1 tw:mt-1 tw:flex tw:items-center tw:px-1 tw:py-1">
            <user-avatar
              :size="36"
              :user="authStore.authUser?.master_staff"
              class="tw:!rounded tw:!transition-all tw:!duration-500"
              rounded="0"
            />

            <div class="tw:ml-3 tw:flex tw:flex-col">
              <span class="tw:text-xs tw:text-on-surface/60"> Impersonator </span>
              <span class="tw:text-sm tw:font-bold">
                {{ authStore.authUser?.master_staff?.first_name }}
                {{ authStore.authUser?.master_staff?.last_name }}
              </span>
            </div>
          </div>

          <v-divider class="tw:my-2" />
        </template>

        <v-list-item
          v-if="canAccessPlatformControl"
          to="/admin-settings"
        >
          <template #prepend>
            <v-icon
              icon="mdi-application-cog"
              size="18"
            />
          </template>

          <v-list-item-title>
            {{ t("generic.navbar.menuUser.adminSettings") }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item to="/settings">
          <template #prepend>
            <v-icon
              icon="mdi-account-cog"
              size="18"
            />
          </template>

          <v-list-item-title>
            {{ t("generic.entity.settings") }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="canShowPermissions"
          :to="{ name: '/settings/[[tab]]', params: { tab: 'permissions' } }"
        >
          <template #prepend>
            <v-icon
              icon="mdi-shield-key-outline"
              size="18"
            />
          </template>

          <v-list-item-title class="tw:!flex tw:!items-center tw:!gap-1.5">
            {{ t("generic.navbar.menuUser.permissions") }}
            <v-chip
              size="x-small"
              variant="flat"
              color="warning"
              density="compact"
              class="tw:!text-[10px] tw:!font-bold"
            >
              {{ t("generic.badges.dev") }}
            </v-chip>
          </v-list-item-title>
        </v-list-item>

        <v-divider class="tw:my-2" />

        <v-list-item to="/whats-new">
          <template #prepend>
            <v-icon
              icon="mdi-newspaper"
              size="18"
            />
          </template>

          <v-list-item-title>
            {{ t("generic.navbar.menuUser.news") }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item @click="emit('open-tutorial')">
          <template #prepend>
            <v-icon
              icon="mdi-help"
              size="18"
            />
          </template>

          <v-list-item-title>
            {{ t("generic.navbar.menuUser.help") }}
          </v-list-item-title>
        </v-list-item>

        <SentryFeedbackButton :use-default-place="false" />

        <v-divider class="tw:my-2" />

        <v-list-item
          v-if="isImpersonating"
          class="destructive"
          data-testid="stop-impersonating-button"
          @click="stopImpersonating"
        >
          <template #prepend>
            <v-icon
              icon="mdi-account-off"
              size="18"
            />
          </template>

          <v-list-item-title>
            {{ t("generic.action.stopImpersonating") }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item
          data-testid="logout-button"
          @click="authStore.userLogout()"
        >
          <template #prepend>
            <v-icon
              icon="mdi-logout"
              size="18"
            />
          </template>

          <v-list-item-title>
            {{ t("generic.navbar.menuUser.logout") }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<style scoped>
.navbar-user-menu-btn :deep(.v-avatar) {
  border-radius: 10px !important;
}

.user-menu-list {
  padding: 8px !important;
  background: transparent !important;
}

.user-menu-list :deep(.v-list-item) {
  min-height: 38px !important;
  padding-block: 2px !important;
  padding-inline: 12px !important;
  border-radius: 10px !important;
}

.user-menu-list :deep(.v-list-item + .v-list-item) {
  margin-top: 1px;
}

.user-menu-list :deep(.v-list-item:hover) {
  background: var(--btn-surface-hover);
}

.user-menu-list :deep(.v-list-item__spacer) {
  width: 0.75rem !important;
}

.user-menu-list :deep(.v-list-item__prepend > .v-icon) {
  opacity: 1;
  color: color-mix(in srgb, rgb(var(--v-theme-on-surface)) 62%, transparent);
}

.user-menu-list :deep(.v-list-item-title) {
  padding-right: 12px;
  font-size: 13.5px;
  font-weight: 650;
  letter-spacing: -0.006em;
  color: var(--btn-ink);
}

.user-menu-list :deep(.v-list-item.destructive),
.user-menu-list :deep(.v-list-item.destructive .v-list-item-title),
.user-menu-list :deep(.v-list-item.destructive .v-icon) {
  color: rgb(var(--v-theme-error));
}
</style>
